import React from 'react';
import WName, {WArticle} from '../WName';
import QL from './QuestionLink';
import PL from '../problems/ProblemLink';
import AL from '../angles/AngleLink';
import NW from '../neologisms/NewWord';
import OML from '../ormaybe/OrMaybeLink';
import LRL from '../longreads/LongReadLink';
import {
  E1, E2, Qual,
  TLDR, lama_toosin, Fibonacci,
} from '../common';

var headings = [
  [<span>Why so many <i>languages</i>?</span>, ""],
  [<span>为什么有这么多语言？</span>, 
    "† Wèishéme yǒu zhème duō yǔyán"],
  [<span>點解有咁多語言？</span>, 
    "† Dim2gaai2 jau5 gam3 do1 jyu5jin4"],
  [<span>Γιατί τόσες πολλές <i>γλώσσες</i>;;;</span>,
    "† Yeti toses polles glosses"],
  [<span>¿Por qué tantos <i>idiomas</i>?</span>, ""],
  [<span>Dlaczego tak wiele <i>języków</i>?</span>, ""],
  [<span>لماذا يوجد الكثير من اللغات؟</span>, 
    "† Limadha yujad alkathir min allughati"],
  [<span>Af hverju eru svona mörg <i>tungumál</i>?</span>, ""],
  [<span>𐒓𐒛 𐒑𐒖𐒄𐒖𐒕 𐒈𐒖𐒁𐒖𐒁𐒂𐒖 𐒐𐒚𐒎𐒖𐒆𐒖𐒔𐒖 𐒁𐒖𐒆𐒖𐒒?</span>, 
    "† Waa maxay sababta luqadaha badan"],
  [<span>इतनी सारी भाषाएं क्यों?</span>, 
    "† Itanee saaree bhaashaen kyon"],
  [<span>ஏன் இத்தனை மொழிகள்?</span>, 
    "† Ēṉ ittaṉai moḻikaḷ"],
  [<span>왜 이렇게 많은 언어가 있나요?</span>, 
    "† Wae ileohge manh-eun eon-eoga issnayo"],
  [<span>Почему так много языков?</span>, 
    "† Pochemu tak mnogo yazikov"],
  [<span>რატომ არსებობს ამდენი ენა?</span>, 
    "† Rat’om arsebobs amdeni ena"],
  [<span>למה יש כל כך הרבה שפות؟</span>, 
    "† Lema yesh kel kech harva shfot"],
  [<span>ทำไมถึงมีภาษามากมายขนาดนี้?</span>, 
    "† Thảmị t̄hụng mī p̣hās̄ʹā mākmāy k̄hnād nī̂"],
  [<span>Miksi niin monta <i>kieltä</i>?</span>, ""],
  [<span>Kwa nini kuna <i>lugha</i> nyingi kabisa?</span>, ""],
  [<span>Pourquoi y a-t-il autant de <i>langues</i>?</span>, ""],
]
var version = Math.random() < 0.33 ? 
  0 : Math.floor(Math.random()*headings.length);

export const languages = {
  name: "languages",
  parentName: headings[version][0],
  prelude: <span>
    They’re not even used <i>correctly</i> <PL name="democracy">more than half</PL> the time.
  </span>,
  question: <span>{headings[version][0]} {(headings[version][1] ? <sup>†</sup> : "")}</span>,
  topCoda: headings[version][1],
  clarification: <span>
    What is this stupid website’s problem with <PL>ENGLISH</PL> ONLY <OML name="offensive">PLZLZLZ???/</OML>
  </span>,
  answerPreview: <div className="answer-preview">
    <p>In short, <PL>English</PL> has become a tool of the <NW>BOSH</NW>.</p>
    <p>English is not <PL name="metaphysics">metaphysically</PL> ‘bad’. Homogenisation is bad. Diversity is a necessary precondition to <i><AL>resilience</AL></i> – which is the route to true safety (not <PL name="safety">Safety<sup>®</sup></PL>: the <NW>BOSH</NW> kind).</p>
  </div>,
  answer: <div className="answer-text">
    <p>Because of the boundaries of <PL name="ch.maire">Máire</PL>’s <LRL>limit</LRL> type 하나 (hana: 1, physical or <AL>biological</AL>), <QL>consciousness</QL> – as it <PL>manifests</PL> at this ‘<NW>level</NW>’ – is necessarily <NW>polygnostic</NW> (many bases of separate knowledge: <AL name="sick.">DID</AL>). To truly <QL name="integration">integrate</QL> (and <LRL>understand</LRL> each ‘<QL>other</QL>’) without just flattening everything to the LCD of <PL>Mamma</PL> <LRL>reactivity</LRL>, we need to <i><PL name="comfort">work</PL></i></p>
  </div>,

  sublist: [

  {
    // según
    name: "segun",
    question: <span>
      Why ‘según’?
    </span>,
    answerPreview: <div name="answer-preview">
      <p>It’s Spanish for ‘according to’. <WName ists /> tend to use it because it’s <i>shorter</i> than the <PL>English</PL> version.</p>
      <p>Granted, by the time wez (we-<NW>zyzzyva</NW>) have to add it all the <i><E1 word="explanation" /></i> (cf. the need to ‘justify’ <PL name="individuality">YourSelf</PL> in <AL>Hell</AL>/<QL>Limiterius</QL>/<QL>Atomland</QL>), it is generally <i>not</i> faster than if wez just wrote ‘according to’ (in <PL>English</PL>).</p>
    </div>,
    answer: <div className="answer-text">
      <p>So it’s an act of résistance. (Kwa <QL name="languages">kiSwahhili</QL>: upinzani.) To resist the homogenising ‘easy’ way of <PL>ENGLISH</PL> ONLY is to stand for <NW>God</NW> in a world where our not-deciding and <QL name="misalignment">not-aligning</QL> has caused us to ‘select’ <AL>the Devil</AL> by default.</p>
      <p>Fight the <NW>BOSH</NW>. Reject the <PL>comfort</PL> of ‘<LRL name="flow">going with the flow</LRL>’. If you are a <NW>POC</NW> with <NW>agency</NW>, <i>choose</i> to help <LRL name="gaia">us all</LRL>. Choose wonder. Choose <LRL name="kujifanya">daring to imagine</LRL>.</p>
    </div>,
  },

  {
    // fingir
    name: "fingir",
    question: <span>
      Why ‘fingir’?
    </span>,
    answerPreview: <div name="answer-preview">
      <p>It has to do with two separate senses of ‘pretend’ used on this site.</p>
      <p>Similar to how the <PL>English</PL> word ‘<NW>explain</NW>’ is used in two senses (<E1 noNW /> and <E2 noNW />, those <QL>differentiated</QL> with ‘superscripts’), the word ‘pretend’ can mean both <LRL>imagination</LRL> and <LRL>lying to ourself</LRL> (cf. <PL>denial</PL>). This site (and what <QL>Gippity</QL> calls the ‘<QL>Pratty</QL> corpus’) tend to use the Swahili word ‘kujifanya’ (used in the maxim ‘<LRL>usiogpe kujifanya</LRL>’) –  which literally translates to ‘make your/oneself’ – for the <i>good</i> kind of ‘pretend’ (when you know this is not your <LRL name="i.">reality</LRL>, but you are <i>problem-solving</i> by <QL name="salvation">actually thinking</QL>, as did <AL name="notnew.p.">Albert Einstein</AL>).</p>
      <p>The ‘<PL name="evil">bad</PL>’ (dishonest) kind of ‘pretend’ is said in Spanish, as ‘fingir’ (‘feen-here’).</p>
    </div>,
    answer: <div className="answer-text">
      <p>¿Hay alguna <i>razón</i> por la que esté escrito en español?<sup>†</sup></p>
      <p>Translation (to GREEK): Υπάρχει κάποιος λόγος που είναι γραμμένο στα ισπανικά;</p>
      <p>You might note that the word ‘razón’ (reason) got translated to ‘λόγος’ (logos), and think about ‘<AL name="fields.">logic</AL>’, logos as a ‘rhetorical’ <i>strategy</i> on the <NW>BOLT</NW> under {lama_toosin}, and reasoning in both Hellenistic and ‘Western’ thought. You can briefly think on how the word ‘Hellenistic’ is closer to the <i>Greek</i> word for Greeks: ελληνικά (ellinika), but few <PL>English</PL>-speakers but ‘scholars’ would even CARE. (From <QL name="reader.">jeetjet</QL>: I CAN’T care no <NW>co-fo</NW> die fat you’re fat you’re UGLY (<PL>you are your body</PL>) stop making me feel DUMB you’re a BAD PERSON stop it <PL name="ban">ban it LIMITATIONS</PL> <Fibonacci reps={[5, 7]} cap delimiter=". " inner_delimiter = ", ">ban it</Fibonacci>)</p>
      <p>You could <i>also</i> notice that the Spanish word ‘escrito’ (written) got translated to ‘γραμμένο’ (grammeno), and that ties into the ‘Tetragrammaton’ (יהוה/JHWH) that is discussed when we compare <WName /> to <i><AL name="religion.">Judaism</AL></i>… but maybe you’ve got the point by now. (‘Show, don’t (<PL name="only">just</PL>) tell’, anyone?)</p>
    </div>,
    bottomFootnote: <span>† Pues, se ve como la palabra <i><PL name="english">inglesa</PL></i> ‘feign’, quizás.</span>,
  },

  {
    // 万件东西
    name: "wanjiandongxi",
    alternatives: "dongxi",
    question: <span>
      万件东西是什么？
    </span>,
    clarification: <span><AL name="notnew.p.quine">Translation</AL> to <i><PL>English</PL></i>: what are 万件东西/wàn jiàn dōngxī?</span>,
    answerPreview: <div name="answer-preview">
      <p>万件东西 (‘wàn jiàn dōngxī’ in Mandarin) means 10,000 things in Chinese.</p>
      <p>东西 (dōngxī) means literally east-west and gives a sense of the things being scattered chaotically all over (cf. <NW>entropy</NW>) that <WName ists /> tend to like because it encapsulates the archetype of <QL name="reader.">jeetjet</QL> (trying, with a single CPU/<QL name="pg.">mind</QL>/<NW>co-fo</NW> to simultaneously manage them <i>all</i>) pretty well.</p>
    </div>,
    answer: <div className="answer-text">
      <p>If you think 万件 (wàn + jiàn) together mean ten thousand – like one means ‘ten’ and the other means ‘thousand’… no. The word ‘万’ (wàn) means 10,000 <i>by itself</i>. Because in China, digits are grouped not in collections of one thousand (e.g. 1,000,000,000: both the number of <i><NW word="moneu">dollars</NW></i> that [insert FAMOUS <PL name="individuality">PERSON</PL><sup>™</sup> here] has, and how many years it might <NW word="okugerocentrism">okugerocentrically</NW>) take to stumble onto a <AL name="framework.">working</AL> <QL>Weltanschauung</QL> without <i><QL name="salvation.">actually thinking</QL></i>). They are grouped into collections of <i>ten thousand</i> (万).</p>
      <p>Because, you know, Chinese is <i>different</i> from <PL>English</PL>… and <QL name="integration">融入不是抹去</QL> if you are <i><QL name="consciousness">conscious</QL></i>: not just <PL>going with the flow</PL> of <AL name="notnew.p.">Heraclitus</AL> river, <QL name="metaphors">rafting</QL> down <NW>entropy</NW>-<LRL name="r.">nature</LRL> to the <NW word="heatdeath">‘inevitable’ heat death</NW> of <AL name="together">our</AL> <LRL name="scope">universe</LRL>. We can <OML name="free will">make a difference</OML>. But not <QL name="isolation">alone</QL>, and not on the <NW>BOLT</NW>. Only through our collective <NW word="overcome">Ganzesystemüberwindung</NW>.</p>
    </div>,
  },

  ],
};
